/**
 * AI-Powered Chair Script Engine
 * Dynamically generates appropriate parliamentary language based on context and rules
 */

import { MOTION_TYPES, FLOW_TYPES, VOTE_REQUIREMENTS } from './motionTypes.js';

function motionMeta(motion) {
  return motion?.type ? MOTION_TYPES[motion.type] : null;
}

/** Chair rules without an assembly vote (not Appeal, which is an assembly question). */
function isChairDecidesIncidentMotion(motion) {
  const m = motionMeta(motion);
  if (!m || m.voteRequired !== VOTE_REQUIREMENTS.CHAIR_DECIDES) return false;
  if (m.flowType === FLOW_TYPES.INFO) return false;
  if (m.flowType === FLOW_TYPES.CHAIR) return true;
  return !m.flowType;
}

/**
 * Meeting Context Analyzer
 * Analyzes the current state and determines what the chair should focus on
 */
class MeetingContextAnalyzer {
  constructor(meetingState) {
    this.state = meetingState;
  }

  /**
   * Determine the primary context of what's happening
   */
  getPrimaryContext() {
    const { currentMotion, motionStack, currentSpeaker, recentActions, pendingUCRequest, isQuorumPresent, currentAgendaItem, quorumStatus } = this.state;

    // Debug logging to understand actual motion statuses
    if (currentMotion) {
      console.log('🔍 AI Chair Debug - Current Motion:', {
        status: currentMotion.status,
        type: currentMotion.type,
        text: currentMotion.text,
        speaker: currentSpeaker
      });
    }

    // Critical issues first - Quorum problems
    const quorumPresent = isQuorumPresent ?? quorumStatus?.present ?? false;

    // Debug logging removed - system working correctly

    if (!quorumPresent) {
      return {
        type: 'no_quorum',
        priority: 'critical',
        urgency: 'immediate',
        currentAgendaItem
      };
    }

    // Pending UC requests take precedence
    if (pendingUCRequest) {
      const requestText =
        typeof pendingUCRequest === 'string'
          ? pendingUCRequest
          : pendingUCRequest.text || pendingUCRequest.request || '';
      return {
        type: 'pending_uc_request',
        priority: 'high',
        urgency: 'immediate',
        request: requestText,
      };
    }

    // Agenda-specific context (when no motion is active)
    if (currentAgendaItem && !currentMotion) {
      const agendaContext = this.getAgendaContext(currentAgendaItem);
      if (agendaContext) {
        return agendaContext;
      }
    }

    // No motion - ready for new business
    if (!currentMotion && (!motionStack || motionStack.length === 0)) {
      return {
        type: 'new_business',
        priority: 'high',
        urgency: this.getRecognitionUrgency()
      };
    }

    // If there's a current motion, analyze its status more comprehensively
    if (currentMotion) {
      const motionType = MOTION_TYPES[currentMotion.type];

      // Motions that do not require a second should never get "do I hear a second?" prompts
      const needsSecond = motionType?.needsSecond !== false;
      if (
        needsSecond &&
        (currentMotion.status === 'pending_second' ||
          currentMotion.status === 'pending-second' ||
          currentMotion.status === 'awaiting_second' ||
          !currentMotion.seconded)
      ) {
        return {
          type: 'awaiting_second',
          priority: 'high',
          motion: currentMotion,
          urgency: 'immediate'
        };
      }

      // Debate closed — put the question (script matches Active Business "Put the question")
      if (currentMotion.status === 'close_debate') {
        const mtPut = motionMeta(currentMotion);
        if (mtPut?.flowType === FLOW_TYPES.INFO) {
          return {
            type: 'info_disposition',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }
        if (currentMotion.type === 'APPEAL') {
          return {
            type: 'appeal_ready_vote',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }
        if (currentMotion.type === 'POINT_ORDER' && currentMotion.chairPutToAssembly) {
          return {
            type: 'point_order_put_to_assembly',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }
        if (isChairDecidesIncidentMotion(currentMotion)) {
          return {
            type: 'chair_ruling_disposition',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }
        if (currentMotion.type === 'AMEND') {
          return {
            type: 'amendment_put_the_question',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }
        return {
          type: 'put_the_question',
          priority: 'high',
          motion: currentMotion,
          urgency: 'immediate'
        };
      }

      // Ready for vote / taking the vote (Aye/No) — only after Put advances status
      if (currentMotion.status === 'ready_for_vote' ||
          currentMotion.status === 'ready-for-vote' ||
          currentMotion.status === 'voting') {

        const mtVote = motionMeta(currentMotion);
        if (mtVote?.flowType === FLOW_TYPES.INFO) {
          return {
            type: 'info_disposition',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }
        if (currentMotion.type === 'APPEAL') {
          return {
            type: 'appeal_ready_vote',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }
        if (currentMotion.type === 'POINT_ORDER' && currentMotion.chairPutToAssembly) {
          return {
            type: 'point_order_put_to_assembly',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }
        if (isChairDecidesIncidentMotion(currentMotion)) {
          return {
            type: 'chair_ruling_disposition',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }

        // Check if debate was specifically closed by Previous Question
        if (currentMotion.debateClosed && currentMotion.debateClosedBy === 'previous_question') {
          return {
            type: 'debate_closed_ready_to_vote',
            priority: 'high',
            motion: currentMotion,
            debateClosedBy: currentMotion.debateClosedBy,
            urgency: 'immediate'
          };
        }

        // Use amendment-specific context for amendments
        if (currentMotion.type === 'AMEND') {
          return {
            type: 'amendment_ready_to_vote',
            priority: 'high',
            motion: currentMotion,
            urgency: 'immediate'
          };
        }

        return {
          type: 'ready_to_vote',
          priority: 'high',
          motion: currentMotion,
          urgency: 'immediate'
        };
      }

      // Tallied but not yet announced (must run BEFORE the broad "seconded && …" branch, else
      // voted_passed still has seconded: true and was misclassified as "stated / open debate").
      const statusStr = String(currentMotion.status || '');
      if (
        statusStr === 'voted_passed' ||
        statusStr === 'voted_failed' ||
        statusStr.startsWith('voted_')
      ) {
        const doneMeta = motionMeta(currentMotion);
        if (doneMeta?.flowType === FLOW_TYPES.INFO) {
          return {
            type: 'info_completed',
            priority: 'high',
            motion: currentMotion,
            result: currentMotion.status,
            urgency: 'immediate'
          };
        }
        if (isChairDecidesIncidentMotion(currentMotion)) {
          return {
            type: 'chair_decides_completed',
            priority: 'high',
            motion: currentMotion,
            result: currentMotion.status,
            urgency: 'immediate'
          };
        }
        if (currentMotion.type === 'APPEAL') {
          return {
            type: 'appeal_completed',
            priority: 'high',
            motion: currentMotion,
            result: currentMotion.status,
            urgency: 'immediate'
          };
        }
        const contextType = currentMotion.type === 'AMEND' ? 'amendment_completed' : 'motion_completed';
        return {
          type: contextType,
          priority: 'high',
          motion: currentMotion,
          result: currentMotion.status,
          urgency: 'immediate'
        };
      }

      // Check for "stated" phase (after second, before debate)
      if (currentMotion.status === 'stated' ||
          currentMotion.status === 'seconded' ||
          (currentMotion.seconded &&
            currentMotion.status !== 'open_debate' &&
            currentMotion.status !== 'ready_for_vote' &&
            currentMotion.status !== 'close_debate' &&
            !statusStr.startsWith('voted_'))) {

        if (motionType?.flowType === FLOW_TYPES.INFO) {
          return {
            type: 'info_item_stated',
            priority: 'high',
            motion: currentMotion,
            urgency: 'high'
          };
        }
        if (isChairDecidesIncidentMotion(currentMotion)) {
          return {
            type: 'chair_decides_stated',
            priority: 'high',
            motion: currentMotion,
            urgency: 'high'
          };
        }

        // Use amendment-specific context for amendments
        if (currentMotion.type === 'AMEND') {
          return {
            type: 'amendment_stated',
            priority: 'high',
            motion: currentMotion,
            debatable: motionType?.debatable,
            urgency: 'high'
          };
        }

        return {
          type: 'motion_stated',
          priority: 'high',
          motion: currentMotion,
          debatable: motionType?.debatable,
          urgency: 'high'
        };
      }

      // Someone is speaking during debate
      if (currentSpeaker) {
        if (motionType?.flowType === FLOW_TYPES.INFO) {
          return {
            type: 'speaker_active_info',
            priority: 'medium',
            speaker: currentSpeaker,
            motion: currentMotion,
            urgency: 'low'
          };
        }
        return {
          type: 'speaker_active',
          priority: 'medium',
          speaker: currentSpeaker,
          motion: currentMotion,
          urgency: 'low'
        };
      }

      // Motion open for debate (no current speaker)
      if (currentMotion.status === 'open_debate' || currentMotion.status === 'debate') {
        if (motionType?.flowType === FLOW_TYPES.INFO) {
          return {
            type: 'info_item_stated',
            priority: 'medium',
            motion: currentMotion,
            urgency: 'medium'
          };
        }
        if (isChairDecidesIncidentMotion(currentMotion)) {
          return {
            type: 'chair_decides_stated',
            priority: 'medium',
            motion: currentMotion,
            urgency: 'medium'
          };
        }
        // Use amendment-specific context for amendments
        if (currentMotion.type === 'AMEND') {
          return {
            type: 'amendment_debate',
            priority: 'medium',
            motion: currentMotion,
            debatable: motionType?.debatable,
            urgency: 'medium'
          };
        }

        return {
          type: 'debate_phase',
          priority: 'medium',
          motion: currentMotion,
          debatable: motionType?.debatable,
          urgency: 'medium'
        };
      }

      // If we have a motion but don't recognize the status, provide motion context
      console.log('⚠️ Unrecognized motion status:', currentMotion.status);
      return {
        type: 'motion_in_progress',
        priority: 'high',
        motion: currentMotion,
        urgency: 'high'
      };
    }

    // Default fallback
    return {
      type: 'new_business',
      priority: 'low',
      urgency: 'low'
    };
  }

  /**
   * Determine how urgently someone needs recognition
   */
  getRecognitionUrgency() {
    // Could analyze factors like:
    // - How long since last motion
    // - Agenda items pending
    // - Member requests for recognition
    return 'medium';
  }

  /**
   * Get contextual factors that should influence language
   */
  getContextualFactors() {
    const factors = {
      hasStack: this.state.motionStack?.length > 0,
      complexMotion: this.isComplexMotion(),
      timeConstraints: this.hasTimeConstraints(),
      proceduralIssues: this.hasProceduralIssues(),
      memberEngagement: this.assessMemberEngagement()
    };

    return factors;
  }

  isComplexMotion() {
    if (!this.state.currentMotion) return false;
    const motionType = MOTION_TYPES[this.state.currentMotion.type];
    return motionType?.class === 'SUBSIDIARY' && ['COMMIT', 'POSTPONE', 'AMEND'].includes(motionType.key);
  }

  hasTimeConstraints() {
    // Could analyze meeting timer, agenda schedule, etc.
    return false;
  }

  hasProceduralIssues() {
    // Could analyze for conflicts, out-of-order motions, etc.
    return false;
  }

  assessMemberEngagement() {
    // Could analyze speaking patterns, participation, etc.
    return 'normal';
  }

  /**
   * Get agenda-specific context for current agenda item
   */
  getAgendaContext(currentAgendaItem) {
    if (!currentAgendaItem || currentAgendaItem.completed) {
      return null;
    }

    const agendaTitle = currentAgendaItem.title.toLowerCase();

    // Map agenda titles to context types
    if (agendaTitle.includes('call to order')) {
      return {
        type: 'agenda_call_to_order',
        priority: 'high',
        urgency: 'high',
        agendaItem: currentAgendaItem
      };
    }

    if (agendaTitle.includes('roll call')) {
      return {
        type: 'agenda_roll_call',
        priority: 'high',
        urgency: 'high',
        agendaItem: currentAgendaItem
      };
    }

    if (agendaTitle.includes('minutes')) {
      return {
        type: 'agenda_minutes',
        priority: 'high',
        urgency: 'medium',
        agendaItem: currentAgendaItem
      };
    }

    if (agendaTitle.includes('report')) {
      return {
        type: 'agenda_reports',
        priority: 'medium',
        urgency: 'medium',
        agendaItem: currentAgendaItem,
        reportType: agendaTitle
      };
    }

    if (agendaTitle.includes('unfinished business')) {
      return {
        type: 'agenda_unfinished_business',
        priority: 'high',
        urgency: 'medium',
        agendaItem: currentAgendaItem
      };
    }

    if (agendaTitle.includes('new business')) {
      return {
        type: 'agenda_new_business',
        priority: 'high',
        urgency: 'medium',
        agendaItem: currentAgendaItem
      };
    }

    if (agendaTitle.includes('election')) {
      return {
        type: 'agenda_elections',
        priority: 'high',
        urgency: 'medium',
        agendaItem: currentAgendaItem
      };
    }

    if (agendaTitle.includes('announcement')) {
      return {
        type: 'agenda_announcements',
        priority: 'medium',
        urgency: 'low',
        agendaItem: currentAgendaItem
      };
    }

    if (agendaTitle.includes('adjournment')) {
      return {
        type: 'agenda_adjournment',
        priority: 'high',
        urgency: 'high',
        agendaItem: currentAgendaItem
      };
    }

    // Generic agenda item
    return {
      type: 'agenda_item',
      priority: 'medium',
      urgency: 'medium',
      agendaItem: currentAgendaItem
    };
  }
}

/**
 * Parliamentary Language Generator
 * Creates appropriate parliamentary language based on context and rules
 */
class ParliamentaryLanguageGenerator {
  constructor() {
    this.languagePatterns = this.initializeLanguagePatterns();
  }

  /**
   * Initialize language patterns for different contexts
   */
  initializeLanguagePatterns() {
    return {
      no_quorum: {
        standard: [
          "We do not have a quorum present. Business cannot proceed until quorum is established.",
          "The chair notes that a quorum is not present. We must wait for additional members.",
          "A quorum is not present. Only privileged motions may be entertained."
        ]
      },

      pending_uc_request: {
        standard: [
          "There is a request for unanimous consent to {request}. Without objection, so ordered.",
          "The chair has a request for unanimous consent: {request}. Is there any objection?",
          "Unanimous consent is requested to {request}. Hearing no objection, it is so ordered."
        ]
      },

      agenda_call_to_order: {
        casual: [
          "Let's get started. It's {time}.",
          "Alright everyone, let's begin. The time is {time}.",
          "OK, let's call this meeting to order at {time}."
        ],
        conversational: [
          "This meeting is called to order at {time}.",
          "Let's call this meeting to order. The time is {time}.",
          "I'm calling this meeting to order at {time}."
        ],
        formal: [
          "The meeting will come to order at {time}.",
          "This meeting is hereby called to order at {time}.",
          "I call this meeting to order. The time is {time}."
        ],
        traditional: [
          "The meeting will please come to order. The time is {time}.",
          "This assembly is hereby called to order at {time}.",
          "The chair calls this meeting to order at {time}."
        ]
      },

      agenda_roll_call: {
        standard: [
          "We will now take roll call. The secretary will call the roll.",
          "The next item of business is roll call. Secretary, please call the roll.",
          "We proceed to roll call. Please respond when your name is called."
        ]
      },

      agenda_minutes: {
        standard: [
          "The next item is the reading of the minutes. Are there any corrections to the minutes?",
          "We proceed to the reading and approval of the minutes. Are there corrections?",
          "The minutes are before you for approval. Are there any corrections or additions?"
        ]
      },

      agenda_reports: {
        standard: [
          "We now move to {reportType}. The chair recognizes the {officer} for their report.",
          "The next item is {reportType}. Will the {officer} please present their report?",
          "We proceed to {reportType}. {Officer}, you have the floor for your report."
        ]
      },

      agenda_unfinished_business: {
        standard: [
          "We now come to unfinished business. What unfinished business do we have?",
          "The next item is unfinished business. Do we have any unfinished business?",
          "We have unfinished business from our previous meeting."
        ]
      },

      agenda_new_business: {
        casual: [
          "What new business do we have?",
          "Any new business?",
          "Let's hear some new business."
        ],
        conversational: [
          "We now come to new business. What new business do we have?",
          "The floor is open for new business.",
          "Let's move to new business. What motions do we have?"
        ],
        formal: [
          "We now proceed to new business. What new business is before us?",
          "The chair will now entertain new business.",
          "We come to new business. What business shall we consider?"
        ],
        traditional: [
          "The assembly will now consider new business. What new business comes before us?",
          "We proceed to the order of new business. What business is to be presented?",
          "The chair will entertain new business from the floor."
        ]
      },

      agenda_elections: {
        standard: [
          "We now proceed to elections. The first order of business is nominations.",
          "The next item is elections. We will begin with the nomination process.",
          "We come to elections. Are there any nominations for the offices to be filled?"
        ]
      },

      agenda_announcements: {
        standard: [
          "We now come to announcements. Does anyone have announcements?",
          "The next item is announcements. What announcements do we have?",
          "Time for announcements. Anyone have something to share?"
        ]
      },

      agenda_adjournment: {
        standard: [
          "Is there any further business? If not, we need a motion to adjourn.",
          "Hearing no further business, I'll accept a motion to adjourn.",
          "If there's no further business, we're ready to adjourn."
        ]
      },

      agenda_item: {
        standard: [
          "We now proceed to {agendaTitle}.",
          "The next item on our agenda is {agendaTitle}.",
          "We come to {agendaTitle}."
        ]
      },

      new_business: {
        recognition: [
          "The floor is open for new business.",
          "What business do we have before us?",
          "The chair will entertain a motion."
        ],
        withMember: [
          "The chair recognizes {member}.",
          "{member}, you have the floor.",
          "The chair recognizes {member} for a motion."
        ]
      },

      awaiting_second: {
        standard: [
          "It has been moved {motion}. Is there a second?",
          "{motion} has been made. Do I hear a second?",
          "A motion has been made {motion}. Is there a second?"
        ],
        urgent: [
          "{motion} needs a second to proceed.",
          "We need a second to {motion}."
        ]
      },

      motion_stated: {
        debatable: [
          "It is moved and seconded {motion}. The motion is open for debate.",
          "It has been moved and seconded {motion}. Discussion on the motion?",
          "The motion {motion} has been moved and seconded. The floor is open for debate."
        ],
        non_debatable: [
          "It is moved and seconded {motion}. The motion is not debatable. Are you ready for the question?",
          "It has been moved and seconded {motion}. This motion requires no debate. Are you ready to vote?"
        ]
      },

      info_item_stated: {
        byType: {
          PARL_INQUIRY: [
            "A parliamentary inquiry has been submitted as follows: “{motion}”. There is no second and no vote; the chair will answer when ready.",
            "The member’s parliamentary inquiry is: “{motion}”. The chair will provide the requested procedural information.",
            "A parliamentary inquiry is pending: “{motion}”. Debate does not apply; the chair responds directly."
          ],
          POINT_INFO: [
            "There is a point of information: “{motion}”. The chair may answer briefly. No second or assembly vote is required.",
            "The member requests information: “{motion}”. The chair may supply the fact or refer the member to the appropriate source.",
            "A point of information has been raised: “{motion}”. This is informational only—the chair responds, there is no vote."
          ],
          CALL_ORDERS: [
            "A call for the orders of the day has been made. The chair will rule whether the assembly is following its adopted order of business. No second or vote is taken on the call itself.",
            "The orders of the day are demanded. The chair determines whether business is in order on the agenda. There is no debate or assembly vote on this call.",
            "We have a demand for the orders of the day. The chair will decide if we are in order; this is not a debatable motion and does not go to a vote."
          ]
        },
        standard: [
          "An informational request is before the chair: “{motion}”. There is no debate and no assembly vote; the chair will respond.",
          "The chair notes a request for information: “{motion}”. Proceed by answering from the chair, then resume pending business."
        ]
      },

      chair_decides_stated: {
        byType: {
          POINT_ORDER: [
            "A point of order has been raised concerning “{motion}”. The chair will rule; there is no second and no debate—only the chair’s decision.",
            "The member makes a point of order: “{motion}”. The chair rules whether procedure has been followed. No assembly vote is taken at this stage.",
            "Point of order: “{motion}”. The chair will decide if the rules have been violated. This is not opened for discussion among the members."
          ],
          QUESTION_PRIVILEGE: [
            "A question of privilege has been raised: “{motion}”. The chair will address the urgency affecting the assembly or a member’s rights. No vote is required unless the chair’s handling is appealed.",
            "The chair hears a question of privilege regarding “{motion}”. The matter is decided by the chair’s ruling unless an appeal is made.",
            "There is a question of privilege: “{motion}”. The chair will rule on the claimed breach; debate among the membership does not apply in the usual way."
          ],
          DIVISION_ASSEMBLY: [
            "The assembly’s division has been called for. The chair will proceed with the standing vote or count as required by the rules.",
            "A division of the assembly is requested. The chair will order the vote taken in a way that clearly ascertains the result.",
            "The chair recognizes the call for a division. The vote will be taken again in a more decisive form as the chair directs."
          ]
        },
        standard: [
          "A procedural matter is before the chair: “{motion}”. The chair decides without an assembly roll call unless the rules provide otherwise.",
          "The chair will resolve this incident: “{motion}”. No general debate applies while the chair determines the proper procedure."
        ]
      },

      info_disposition: {
        byType: {
          PARL_INQUIRY: [
            "The chair will respond to the parliamentary inquiry. No vote is taken on an inquiry.",
            "In response to the member's inquiry, the chair states the following.",
            "The chair addresses the parliamentary inquiry raised. We then return to the business that was interrupted."
          ],
          POINT_INFO: [
            "The chair will answer the point of information. There is no second, no debate, and no vote on this.",
            "In response to the request for information, the chair states the following.",
            "The chair addresses the point of information raised; the pending question is not affected."
          ],
          CALL_ORDERS: [
            "The chair will rule on the call for the orders of the day. No assembly vote is required on the call itself.",
            "The call for the orders of the day is before the chair. We will either resume the agenda or continue as ruled.",
            "The chair addresses the demand for the orders of the day. There is no vote on this procedural question."
          ]
        },
        standard: [
          "The chair will respond to this item. No vote of the assembly applies.",
          "This is an informational matter before the chair; we then resume the pending question."
        ]
      },

      chair_ruling_disposition: {
        byType: {
          POINT_ORDER: [
            "A point of order has been raised. The chair will rule on the point of order.",
            "A point of order has been raised. The chair will now rule on the point.",
            "A point of order has been raised. The chair will rule whether the point is well taken."
          ],
          QUESTION_PRIVILEGE: [
            "The chair will rule on the question of privilege. No assembly vote is required unless the ruling is appealed.",
            "The question of privilege is before the chair for a ruling.",
            "The chair rules on the question of privilege raised, unless an appeal is taken from that ruling."
          ],
          DIVISION_ASSEMBLY: [
            "A division is called for. We will retake the vote by a standing count so the result is clear.",
            "The division is granted. Members in favor will please rise; then those opposed.",
            "A division is in order. We will retake this vote so it may be properly counted."
          ]
        },
        standard: [
          "The chair will rule on this matter. There is no assembly vote unless the ruling is appealed.",
          "This question is before the chair for a ruling."
        ]
      },

      // The chair has chosen to submit the point of order to the assembly
      // rather than rule alone (RONR ~23:1-9).
      point_order_put_to_assembly: [
        "The chair submits the point of order to the assembly. Should the point of order be well taken, or not well taken?",
        "Should the point of order be well taken or not well taken? The assembly will decide by vote.",
        "The question is on the point of order: is it well taken, or not well taken? Those in favor of well taken, say aye; those opposed, say no."
      ],

      appeal_ready_vote: {
        standard: [
          "The question on the appeal is: Shall the decision of the chair be sustained? All those in favor of sustaining the chair’s decision, say aye; opposed, say no. A tie vote sustains the chair.",
          "We come to the appeal. The assembly will decide whether the chair was correct. Those who believe the chair’s decision should stand, vote aye; those who believe it should be reversed, vote no. A tie sustains the chair.",
          "The pending question is whether the ruling of the chair stands. All in favor of sustaining the chair, say aye; opposed, say no. Note: a tie sustains the chair."
        ]
      },

      appeal_completed: {
        passed: [
          "The ayes have it—or the tie sustains the chair—and the decision of the chair is sustained.",
          "The decision of the chair is sustained. The ruling stands as given.",
          "The chair’s decision is sustained. Proceed in accordance with that ruling."
        ],
        failed: [
          "The noes have it—the decision of the chair is overruled—and the chair will comply with the assembly’s judgment.",
          "The decision of the chair is overruled. The chair will apply the assembly’s reversal.",
          "The chair is overruled. Proceed as the assembly has directed on the underlying point."
        ]
      },

      speaker_active_info: [
        "{speaker} is recognized on the parliamentary inquiry regarding “{motion}”.",
        "{speaker} has the floor for this informational request about “{motion}”.",
        "The member {speaker} is addressing the inquiry: “{motion}”."
      ],

      info_completed: {
        byType: {
          PARL_INQUIRY: [
            "The parliamentary inquiry is closed. We return to the pending question.",
            "The chair has answered the inquiry. Pending business resumes.",
            "The inquiry is discharged. Let us return to the question that was before the assembly."
          ],
          POINT_INFO: [
            "The point of information is answered and closed. Pending business continues.",
            "That concludes the point of information. We resume the interrupted question.",
            "The information has been given. Return to the pending motion or business."
          ],
          CALL_ORDERS: [
            "The call for the orders of the day is disposed of. Proceed in accordance with the chair’s ruling.",
            "The ruling on the orders of the day is noted. Continue the meeting as directed.",
            "Orders of the day are settled. Resume the proper item on the agenda."
          ]
        },
        standard: [
          "This informational matter is closed. We return to pending business.",
          "The chair has disposed of the request. Resume the question that was pending."
        ]
      },

      chair_decides_completed: {
        passed: {
          byType: {
            POINT_ORDER: [
              "The point of order is well taken, and the meeting will proceed in conformity with that ruling.",
              "The point of order is well taken, and we will correct our proceedings accordingly.",
              "The point of order is well taken, and business will continue as the ruling requires."
            ],
            QUESTION_PRIVILEGE: [
              "The question of privilege is sustained as ruled. We resume the business before the assembly.",
              "The chair’s disposition of the privilege is as announced. Pending business continues.",
              "The privilege matter is resolved in favor of the ruling stated. Return to the floor’s prior business."
            ],
            DIVISION_ASSEMBLY: [
              "The division is granted and completed as ordered. Record the result of the retaken vote.",
              "The chair has carried out the division. The assembly’s vote stands as finally determined.",
              "The division procedure is complete. Proceed with the declared result."
            ]
          },
          standard: [
            "The chair’s ruling stands as announced. Resume pending business.",
            "That incidental is disposed of by the chair. Return to the question that was pending."
          ]
        },
        failed: {
          byType: {
            POINT_ORDER: [
              "The point of order is not well taken, and the pending business will continue as before.",
              "The point of order is not well taken, and we will proceed with the business that was interrupted.",
              "The point of order is not well taken, and the meeting continues without further correction."
            ],
            QUESTION_PRIVILEGE: [
              "The privilege claim is not sustained as made. We return to regular business.",
              "The chair does not grant the privilege as requested. Resume the pending question.",
              "On the question of privilege, the ruling is against the request. Proceed with the prior business."
            ],
            DIVISION_ASSEMBLY: [
              "The division request is denied or satisfied otherwise as the chair ruled. Continue with the vote as determined.",
              "The chair declines this division or handles it as announced. Proceed accordingly.",
              "The matter of division is closed without further tally as ruled. Resume business."
            ]
          },
          standard: [
            "The chair’s ruling is recorded in the negative on that incident. Pending business resumes.",
            "That request is not granted. Return to the pending question."
          ]
        }
      },

      debate_phase: {
        opening: [
          "The question is on {motion}. The motion is open for debate.",
          "We have the motion {motion} before us. Discussion?",
          "The floor is open for debate on {motion}."
        ],
        non_debatable: [
          "The motion {motion} is not debatable. We proceed directly to the vote.",
          "This motion requires no debate. Are you ready to vote?"
        ]
      },

      ready_to_vote: {
        standard: [
          "The question is on {motion}. All those in favor say Aye; those opposed, say No.",
          "The question is on the motion. All those in favor say Aye; those opposed, say No.",
          "We are now voting on {motion}. All those in favor say Aye; those opposed, say No."
        ]
      },
      put_the_question: {
        standard: [
          "Debate is closed. The question is on {motion}. Put the question.",
          "Debate is closed. Are you ready for the question on {motion}?",
          "The question is on {motion}. The chair will put the question."
        ]
      },
      amendment_put_the_question: {
        standard: [
          "Debate on the amendment is closed. The question is on {motion}. Put the question.",
          "Debate is closed. Are you ready for the question on the amendment {motion}?",
          "The question is on {motion}. The chair will put the question on the amendment."
        ]
      },
      debate_closed_ready_to_vote: {
        standard: [
          "Debate has been closed by Previous Question. The question is on the motion {motion}. All those in favor say aye, those opposed, say no.",
          "Previous Question has been adopted. The question is on the motion {motion}. All those in favor say aye, those opposed, say no.",
          "Debate is closed. The question is on the motion {motion}. All those in favor say aye, those opposed, say no."
        ]
      },

      // Amendment-specific patterns
      amendment_stated: {
        debatable: [
          "It is moved and seconded {motion}. The amendment is open for debate.",
          "An amendment has been moved and seconded {motion}. Discussion on the amendment?",
          "{motion} has been moved and seconded. The floor is open for debate on the amendment."
        ],
        non_debatable: [
          "It is moved and seconded {motion}. The amendment is not debatable. Are you ready for the question?",
          "An amendment has been moved and seconded {motion}. This amendment requires no debate. Are you ready to vote?"
        ]
      },

      amendment_debate: {
        opening: [
          "The question is on {motion}. The amendment is open for debate.",
          "We have {motion} before us. Discussion on the amendment?",
          "{motion} is now open for debate. The floor recognizes speakers on the amendment."
        ]
      },

      amendment_ready_to_vote: {
        standard: [
          "The question is on the amendment. All those in favor say aye, those opposed, say no.",
          "The question is on {motion}. All those in favor say aye, those opposed, say no.",
          "Are you ready for the question on the amendment? The question is on the amendment. All those in favor say aye, those opposed, say no."
        ]
      },

      voting_phase: {
        standard: [
          "The question is on the motion. All those in favor say aye, those opposed, say no.",
          "The question is on the motion {motion}. All those in favor say aye, those opposed, say no.",
          "The question is on {motion}. All those in favor say aye, those opposed, say no."
        ],
        special_majority: [
          "This motion requires {majority}. The question is on the motion. All those in favor say aye, those opposed, say no.",
          "A {majority} vote is required. The question is on the motion. All those in favor say aye, those opposed, say no."
        ]
      },

      speaker_active: [
        "{speaker} has the floor on {motion}.",
        "The member {speaker} is speaking.",
        "{speaker} is addressing the motion."
      ],

      motion_completed: {
        passed: [
          "The ayes have it and the motion is adopted.",
          "The ayes have it, and the motion regarding “{motion}” is adopted.",
          "The motion is adopted."
        ],
        failed: [
          "The noes have it, and the motion is lost.",
          "The noes have it, and the motion regarding “{motion}” is lost.",
          "The motion is lost."
        ]
      },

      amendment_completed: {
        passed: [
          "The ayes have it and the amendment is adopted.",
          "The ayes have it, and the amendment is adopted.",
          "The amendment is adopted."
        ],
        failed: [
          "The noes have it, and the amendment is lost.",
          "The noes have it and the amendment is lost.",
          "The amendment is lost."
        ]
      },

      motion_in_progress: {
        standard: [
          "The motion {motion} is before us.",
          "We have the motion {motion} to consider.",
          "The current motion is {motion}."
        ]
      },

      general_meeting: [
        "The floor is open for new business.",
        "What business shall we take up?",
        "Is there any new business?"
      ]
    };
  }

  /**
   * Generate appropriate language based on context
   */
  generateScript(context, factors = {}) {

    const patterns = this.languagePatterns[context.type];
    if (!patterns) return this.getDefaultScript(context);

    let selectedPattern;

    switch (context.type) {
      case 'no_quorum':
        selectedPattern = this.selectRandomPattern(patterns.standard);
        break;

      case 'pending_uc_request':
        selectedPattern = this.selectRandomPattern(patterns.standard);
        break;

      case 'agenda_call_to_order':
      case 'agenda_roll_call':
      case 'agenda_minutes':
      case 'agenda_reports':
      case 'agenda_unfinished_business':
      case 'agenda_new_business':
      case 'agenda_elections':
      case 'agenda_announcements':
      case 'agenda_adjournment':
      case 'agenda_item':
        selectedPattern = this.selectPatternByFormality(patterns, context, factors);
        break;

      case 'new_business':
        selectedPattern = this.selectNewBusinessPattern(context, patterns, factors);
        break;

      case 'awaiting_second':
        selectedPattern = this.selectAwaitingSecondPattern(context, patterns, factors);
        break;

      case 'info_item_stated':
        selectedPattern = this.selectInfoItemStatedPattern(context, patterns, factors);
        break;

      case 'chair_decides_stated':
        selectedPattern = this.selectChairDecidesStatedPattern(context, patterns, factors);
        break;

      case 'info_disposition':
        selectedPattern = this.selectInfoDispositionPattern(context, patterns, factors);
        break;

      case 'chair_ruling_disposition':
        selectedPattern = this.selectChairRulingDispositionPattern(context, patterns, factors);
        break;

      case 'appeal_ready_vote':
        selectedPattern = this.selectRandomPattern(patterns.standard);
        break;

      case 'speaker_active_info':
        selectedPattern = this.selectRandomPattern(patterns);
        break;

      case 'info_completed':
        selectedPattern = this.selectInfoCompletedPattern(context, patterns, factors);
        break;

      case 'chair_decides_completed':
        selectedPattern = this.selectChairDecidesCompletedPattern(context, patterns, factors);
        break;

      case 'appeal_completed':
        selectedPattern =
          context.result === 'voted_passed'
            ? this.selectRandomPattern(patterns.passed)
            : this.selectRandomPattern(patterns.failed);
        break;

      case 'motion_stated':
        selectedPattern = this.selectMotionStatedPattern(context, patterns, factors);
        break;

      case 'debate_phase':
        selectedPattern = this.selectDebatePattern(context, patterns, factors);
        break;

      case 'ready_to_vote':
        selectedPattern = this.selectReadyToVotePattern(context, patterns, factors);
        break;
      case 'put_the_question':
        selectedPattern = this.selectPutTheQuestionPattern(context, patterns, factors);
        break;
      case 'amendment_put_the_question':
        selectedPattern = this.selectAmendmentPutTheQuestionPattern(context, patterns, factors);
        break;
      case 'debate_closed_ready_to_vote':
        selectedPattern = this.selectDebateClosedReadyToVotePattern(context, patterns, factors);
        break;

      case 'amendment_stated':
        selectedPattern = this.selectAmendmentStatedPattern(context, patterns, factors);
        break;
      case 'amendment_debate':
        selectedPattern = this.selectAmendmentDebatePattern(context, patterns, factors);
        break;
      case 'amendment_ready_to_vote':
        selectedPattern = this.selectAmendmentReadyToVotePattern(context, patterns, factors);
        break;

      case 'voting_phase':
        selectedPattern = this.selectVotingPattern(context, patterns, factors);
        break;

      case 'speaker_active':
        selectedPattern = this.selectSpeakerPattern(context, patterns, factors);
        break;

      case 'motion_completed':
        selectedPattern = this.selectCompletedPattern(context, patterns, factors);
        break;

      case 'amendment_completed':
        selectedPattern = this.selectAmendmentCompletedPattern(context, patterns, factors);
        break;

      case 'motion_in_progress':
        selectedPattern = this.selectMotionInProgressPattern(context, patterns, factors);
        break;

      default:
        selectedPattern = this.selectRandomPattern(patterns);
    }

    return this.populateTemplate(selectedPattern, context);
  }

  selectNewBusinessPattern(context, patterns, factors) {
    if (context.recognizedMember) {
      return this.selectRandomPattern(patterns.withMember);
    }
    return this.selectRandomPattern(patterns.recognition);
  }

  selectAwaitingSecondPattern(context, patterns, factors) {
    if (context.urgency === 'immediate' || factors.timeConstraints) {
      return this.selectRandomPattern(patterns.urgent);
    }
    return this.selectRandomPattern(patterns.standard);
  }

  selectByTypeOrStandard(context, patterns) {
    const key = context.motion?.type;
    if (patterns.byType?.[key]) {
      return this.selectRandomPattern(patterns.byType[key]);
    }
    return this.selectRandomPattern(patterns.standard);
  }

  selectInfoItemStatedPattern(context, patterns, factors) {
    return this.selectByTypeOrStandard(context, patterns);
  }

  selectChairDecidesStatedPattern(context, patterns, factors) {
    return this.selectByTypeOrStandard(context, patterns);
  }

  selectInfoDispositionPattern(context, patterns, factors) {
    return this.selectByTypeOrStandard(context, patterns);
  }

  selectChairRulingDispositionPattern(context, patterns, factors) {
    return this.selectByTypeOrStandard(context, patterns);
  }

  selectInfoCompletedPattern(context, patterns, factors) {
    return this.selectByTypeOrStandard(context, patterns);
  }

  selectChairDecidesCompletedPattern(context, patterns, factors) {
    const branch = context.result === 'voted_passed' ? patterns.passed : patterns.failed;
    return this.selectByTypeOrStandard(context, branch);
  }

  selectMotionStatedPattern(context, patterns, factors) {
    if (!context.debatable) {
      return this.selectRandomPattern(patterns.non_debatable);
    }
    return this.selectRandomPattern(patterns.debatable);
  }

  selectDebatePattern(context, patterns, factors) {
    if (!context.debatable) {
      return this.selectRandomPattern(patterns.non_debatable);
    }
    return this.selectRandomPattern(patterns.opening);
  }

  selectReadyToVotePattern(context, patterns, factors) {
    return this.selectRandomPattern(patterns.standard);
  }

  selectPutTheQuestionPattern(context, patterns, factors) {
    return this.selectRandomPattern(patterns.standard);
  }

  selectAmendmentPutTheQuestionPattern(context, patterns, factors) {
    return this.selectRandomPattern(patterns.standard);
  }

  selectDebateClosedReadyToVotePattern(context, patterns, factors) {
    return this.selectRandomPattern(patterns.standard);
  }

  selectAmendmentStatedPattern(context, patterns, factors) {
    if (!context.debatable) {
      return this.selectRandomPattern(patterns.non_debatable);
    }
    return this.selectRandomPattern(patterns.debatable);
  }

  selectAmendmentDebatePattern(context, patterns, factors) {
    return this.selectRandomPattern(patterns.opening);
  }

  selectAmendmentReadyToVotePattern(context, patterns, factors) {
    return this.selectRandomPattern(patterns.standard);
  }

  selectVotingPattern(context, patterns, factors) {
    const motionType = MOTION_TYPES[context.motion?.type];
    if (motionType?.voteRequired === 'none') {
      return this.selectRandomPattern(patterns.standard);
    }
    if (motionType?.voteRequired !== 'majority' && motionType?.voteRequired !== 'MAJORITY') {
      return this.selectRandomPattern(patterns.special_majority);
    }
    return this.selectRandomPattern(patterns.standard);
  }

  selectSpeakerPattern(context, patterns, factors) {
    return this.selectRandomPattern(patterns);
  }

  selectCompletedPattern(context, patterns, factors) {
    if (context.result === 'voted_passed') {
      return this.selectRandomPattern(patterns.passed);
    }
    return this.selectRandomPattern(patterns.failed);
  }

  selectAmendmentCompletedPattern(context, patterns, factors) {
    if (context.result === 'voted_passed') {
      return this.selectRandomPattern(patterns.passed);
    }
    return this.selectRandomPattern(patterns.failed);
  }

  selectMotionInProgressPattern(context, patterns, factors) {
    return this.selectRandomPattern(patterns.standard);
  }

  selectRandomPattern(patterns) {
    if (!Array.isArray(patterns)) {
      return patterns;
    }
    if (patterns.length === 0) return '';
    if (patterns.length === 1) return patterns[0];
    // Deterministic pick so re-renders / remote sync don't cycle synonymous lines
    const state = this.meetingState || {};
    const motion = state.currentMotion;
    const agenda = state.currentAgendaItem;
    const seed = [
      motion?.id ?? '',
      motion?.status ?? '',
      motion?.type ?? '',
      agenda?.id ?? '',
      agenda?.title ?? '',
      state.pendingUCRequest?.id ?? '',
      state.currentSpeaker?.id ?? state.currentSpeaker?.name ?? '',
      String(state.meetingSetup?.presentMembers ?? ''),
    ].join('|');
    let hash = 2166136261;
    for (let i = 0; i < seed.length; i += 1) {
      hash ^= seed.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return patterns[Math.abs(hash) % patterns.length];
  }

  selectPatternByFormality(patterns, context, factors) {
    // Use conversational tone as default
    let formalityPatterns = patterns.conversational || patterns.standard || patterns.formal || patterns.casual || patterns.traditional;

    // If we still don't have patterns, get the first available set
    if (!formalityPatterns) {
      const availableKeys = Object.keys(patterns);
      if (availableKeys.length > 0) {
        formalityPatterns = patterns[availableKeys[0]];
      }
    }

    return this.selectRandomPattern(formalityPatterns);
  }

  /**
   * Replace template variables with actual values
   */
  populateTemplate(template, context) {
    let result = template;

    // Replace motion text with appropriate terminology
    if (context.motion?.text) {
      // Use proper terminology for amendments
      if (context.motion.type === 'AMEND') {
        result = result.replace(/{motion}/g, `the amendment: "${context.motion.text}"`);
        // Also replace any generic "motion" references with "amendment"
        result = result.replace(/\bmotion\b/g, 'amendment');
        result = result.replace(/\bMotion\b/g, 'Amendment');
      } else if (context.motion.type === 'MAIN' && context.motion.isAmended) {
        result = result.replace(
          /{motion}/g,
          `the main motion as amended: "${context.motion.text}"`
        );
      } else {
        result = result.replace(/{motion}/g, context.motion.text);
      }
    }

    // Replace member/speaker names
    if (context.recognizedMember) {
      result = result.replace(/{member}/g, context.recognizedMember);
    }
    if (context.speaker) {
      result = result.replace(/{speaker}/g, context.speaker);
    }

    // Replace UC request text
    if (context.request != null && context.request !== '') {
      const requestText =
        typeof context.request === 'string'
          ? context.request
          : context.request.text || context.request.request || '';
      result = result.replace(/{request}/g, requestText);
    }

    // Replace agenda-specific variables
    if (context.agendaItem) {
      // Call-to-order lines use {time}: after the meeting timer "Start" runs, freeze to that moment.
      const startRaw = this.meetingState?.meetingStartTime;
      const useRecordedStart =
        context.type === 'agenda_call_to_order' && startRaw != null;
      const timeDate = useRecordedStart
        ? startRaw instanceof Date
          ? startRaw
          : new Date(startRaw)
        : new Date();
      const timeString = timeDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      result = result.replace(/{time}/g, timeString);

      // Agenda title
      result = result.replace(/{agendaTitle}/g, context.agendaItem.title);

      // Report type and officer for reports
      if (context.reportType) {
        let reportType = context.reportType;
        let officer = 'officer';

        if (reportType.includes('president')) {
          officer = 'President';
          reportType = 'President\'s Report';
        } else if (reportType.includes('secretary')) {
          officer = 'Secretary';
          reportType = 'Secretary\'s Report';
        } else if (reportType.includes('treasurer')) {
          officer = 'Treasurer';
          reportType = 'Treasurer\'s Report';
        } else if (reportType.includes('committee')) {
          officer = 'Committee Chair';
          reportType = 'Committee Reports';
        }

        result = result.replace(/{reportType}/g, reportType);
        result = result.replace(/{officer}/g, officer);
        result = result.replace(/{Officer}/g, officer); // Capitalized version
      }
    }

    // Replace vote requirements
    if (context.motion) {
      const motionType = MOTION_TYPES[context.motion.type];
      if (motionType?.voteRequired === 'TWO_THIRDS') {
        result = result.replace(/{majority}/g, 'a two-thirds majority');
      } else if (motionType?.voteRequired === 'UNANIMOUS') {
        result = result.replace(/{majority}/g, 'unanimous consent');
      }

      // Motions that do NOT require a second should not be announced as "moved and seconded".
      if (motionType?.needsSecond === false) {
        result = result
          .replace(/\bIt is moved and seconded\b/g, 'It is moved')
          .replace(/\bIt has been moved and seconded\b/g, 'It has been moved')
          .replace(/\bhas been moved and seconded\b/g, 'has been moved')
          .replace(/\bmoved and seconded\b/g, 'moved')
          .replace(/\bneeds a second to proceed\b/g, 'is before us')
          .replace(/\bDo I hear a second\?\b/g, '');
      }
    }

    return result;
  }

  getDefaultScript(context) {
    return "What business do we have before us?";
  }
}

/**
 * Main AI Chair Script Engine
 */
export class AIChairScriptEngine {
  constructor() {
    this.languageGenerator = new ParliamentaryLanguageGenerator();
  }

  /**
   * Generate chair script for current meeting state
   */
  generateChairScript(meetingState) {
    try {
      // Store meeting state for language generator access
      this.languageGenerator.meetingState = meetingState;

      // Analyze current context
      const analyzer = new MeetingContextAnalyzer(meetingState);
      const context = analyzer.getPrimaryContext();
      const factors = analyzer.getContextualFactors();

      // Generate appropriate language
      const script = this.languageGenerator.generateScript(context, factors);

      return script;
    } catch (error) {
      console.error('Error generating chair script:', error);
      return "What business do we have before us?";
    }
  }

  /**
   * Get contextual help for the chair
   */
  getContextualHelp(meetingState) {
    const analyzer = new MeetingContextAnalyzer(meetingState);
    const context = analyzer.getPrimaryContext();

    const helpMap = {
      'no_quorum': 'Quorum not present - cannot conduct business',
      'pending_uc_request': 'Unanimous consent request pending',
      'new_business': 'Ready to recognize members for new motions',
      'awaiting_second': 'Motion needs a second before it can be considered',
      'info_item_stated': 'Informational or procedural request — chair responds, no assembly vote',
      'chair_decides_stated': 'Chair rules on this incidental — no debate or roll call unless appealed',
      'info_disposition': 'Chair answers or disposes — no ayes/noes on the inquiry itself',
      'chair_ruling_disposition': 'Record the chair’s ruling on this point',
      'appeal_ready_vote': 'Assembly votes whether to sustain the chair’s decision',
      'speaker_active_info': 'Member speaking on an informational request',
      'info_completed': 'Inquiry closed — resume pending business',
      'chair_decides_completed': 'Chair’s ruling on the incidental is recorded',
      'appeal_completed': 'Appeal decided — chair sustained or overruled',
      'debate_phase': 'Motion is open for discussion',
      'voting_phase': 'Ready to take a vote on the motion',
      'speaker_active': 'A member is currently speaking',
      'motion_completed': 'Motion has been decided, ready for next business'
    };

    return helpMap[context.type] || 'Managing meeting proceedings';
  }
}

/**
 * Merge latest motion text from `motions` (e.g. after amendment) so chair script matches the floor.
 */
function resolveCurrentMotionForScript(currentMotion, motions) {
  if (!currentMotion || !Array.isArray(motions) || motions.length === 0) return currentMotion;
  const fresh = motions.find((m) => m.id === currentMotion.id);
  if (!fresh) return currentMotion;
  return {
    ...currentMotion,
    text: fresh.text,
    originalText: fresh.originalText ?? currentMotion.originalText,
    isAmended: fresh.isAmended ?? currentMotion.isAmended,
    amendments: fresh.amendments ?? currentMotion.amendments,
  };
}

// Export the main function for backwards compatibility
export const generateAIChairScript = (meetingState) => {
  const resolved = {
    ...meetingState,
    currentMotion: resolveCurrentMotionForScript(meetingState.currentMotion, meetingState.motions),
  };
  const engine = new AIChairScriptEngine();
  return engine.generateChairScript(resolved);
};

export default generateAIChairScript;